/**
 * 字符串处理工具函数集合
 * 用于统一管理字符串相关的通用操作
 */

/**
 * 分割申请人字符串（支持复杂括号嵌套）
 * 处理著作权人名称中可能包含括号的情况，避免错误分割
 * @param {string} applicant - 申请人字符串，多个申请人用顿号分隔
 * @param {string} regex - 分隔符，默认为中文顿号 '、'
 * @returns {Array<string>} - 分割后的申请人名称数组
 * @example
 * splitApplicant('张三（北京）、李四')
 * // 返回: ['张三（北京）', '李四']
 */
export const splitApplicant = (applicant, regex = '、') => {
  // 容错处理：如果入参为空，返回空数组而不是抛出异常
  if (!applicant) {
    return [];
  }

  const split = applicant.split(regex);
  const names = [];
  let num = 0; // 括号嵌套层级计数器
  let currentName = ''; // 当前正在拼接的名称

  for (const s of split) {
    currentName += s + regex;

    // 统计当前片段中的括号数量
    for (let i = 0; i < s.length; i++) {
      const ch = s.charAt(i);
      if (ch === '（' || ch === '(') {
        num += 1; // 遇到左括号，层级+1
      }
      if (ch === '）' || ch === ')') {
        num -= 1; // 遇到右括号，层级-1
      }
    }

    // 括号匹配完成（层级归零），说明一个完整名称结束
    if (num === 0) {
      currentName = currentName.slice(0, -1); // 移除最后一个分隔符
      names.push(currentName);
      currentName = '';
    } else if (num > 0) {
      // 括号未闭合，继续拼接下一段
      continue;
    } else {
      // num < 0 说明右括号多余，强制结束当前名称
      currentName = currentName.slice(0, -1);
      names.push(currentName);
      currentName = '';
      num = 0; // 重置计数器
    }
  }

  // 处理剩余的未完成名称
  if (currentName.length > 0) {
    currentName = currentName.slice(0, -1);
    names.push(currentName);
  }

  return names;
};

/**
 * 生成随机字符串（字母+数字）
 * @param {number} length - 字符串长度
 * @returns {string} - 随机字符串
 * @example
 * generateRandomString(6) // 'aB3xY9'
 */
export const generateRandomString = (length) => {
  const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters[randomIndex];
  }
  return result;
};

/**
 * 生成指定位数的随机数字字符串（不足位数前补0）
 * @param {number} length - 数字位数
 * @returns {string} - 随机数字字符串
 * @example
 * generateRandomNumber(5) // '03847'
 */
export const generateRandomNumber = (length) => {
  let randomNumber = Math.random() * 100000;
  randomNumber = Math.floor(randomNumber);
  return randomNumber.toString().padStart(length, '0');
};

/**
 * 生成唯一ID（用于列表项的key）
 * @returns {string} - 6位随机字符串ID
 * @example
 * generateId() // 'aB3xY9'
 */
export const generateId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * 递归地去除对象中所有字符串值的首尾空格
 * 注意：会修改原对象
 * @param {Object} obj - 待处理的对象
 * @returns {Object} - 处理后的对象（与入参是同一个引用）
 * @example
 * trimStringValues({ name: '  张三  ', age: 20 })
 * // 返回: { name: '张三', age: 20 }
 */
export const trimStringValues = (obj) => {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (typeof obj[key] === 'string') {
        obj[key] = obj[key].trim();
      }
    }
  }
  return obj;
};

/**
 * 复制文本到剪贴板（兼容性实现）
 * @param {string} value - 待复制的文本内容
 * @param {Function} successCallback - 复制成功的回调函数
 * @example
 * copyToClipboard('测试文本', () => {
 *   console.log('复制成功')
 * })
 */
export const copyToClipboard = (value, successCallback) => {
  // 创建临时textarea元素
  const textarea = document.createElement("textarea");

  // 设置为只读，避免点击复制时屏幕抖动
  textarea.setAttribute("readonly", "readonly");

  // 设置复制内容
  textarea.value = value;

  // 添加到DOM
  document.body.appendChild(textarea);

  // 选择内容
  textarea.select();

  // 执行复制命令
  document.execCommand("Copy");

  // 移除临时元素
  document.body.removeChild(textarea);

  // 执行成功回调
  if (successCallback && typeof successCallback === 'function') {
    successCallback();
  }
};

/**
 * 设置著作权人显示名称（带授权状态）
 * @param {string} name - 著作权人名称
 * @param {string} loginName - 登录账号名称
 * @returns {string} - 格式化后的显示名称
 * @example
 * setDisplayName('张三', 'zhangsan')  // '张三(zhangsan)'
 * setDisplayName('张三', '')          // '张三(未授权)'
 */
export const setDisplayName = (name, loginName) => {
  let displayName = name;
  if (name) {
    if (loginName) {
      displayName = name + '(' + loginName + ')';
    } else {
      displayName = name + '(未授权)';
    }
  }
  return displayName;
};
